/** INFRA auth-service client. */

const AUTH_BASE = import.meta.env.VITE_AUTH_URL || "https://auth.mattherbert.ca";

export interface AuthUser {
  id: string;
  email: string;
  name: string;
  role: string;
  product: string;
}

/** Get current user from token (cookie or localStorage). */
export async function getMe(): Promise<AuthUser | null> {
  try {
    const token = localStorage.getItem("infra_token");
    const headers: Record<string, string> = {};
    if (token) headers["authorization"] = `Bearer ${token}`;

    const res = await fetch(`${AUTH_BASE}/auth/me`, {
      credentials: "include",
      headers,
    });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

/** Redirect to OAuth login. */
export function login(provider: "google" | "github" | "keycloak" = "google") {
  const redirectUri = `${window.location.origin}/`;
  window.location.href = `${AUTH_BASE}/auth/${provider}/login?product=ai&redirect_uri=${encodeURIComponent(redirectUri)}`;
}

/** Email/password login. */
export async function emailLogin(email: string, password: string): Promise<{ token: string; user: AuthUser }> {
  const res = await fetch(`${AUTH_BASE}/auth/login`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password, product: "ai" }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Invalid email or password.");
  localStorage.setItem("infra_token", data.token);
  return data;
}

/** Email/password registration. */
export async function emailRegister(email: string, password: string, name?: string): Promise<{ token: string; user: AuthUser }> {
  const res = await fetch(`${AUTH_BASE}/auth/register`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ email, password, name, product: "ai" }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Registration failed.");
  localStorage.setItem("infra_token", data.token);
  return data;
}

/** Logout. */
export async function logout() {
  const token = localStorage.getItem("infra_token");
  const headers: Record<string, string> = {};
  if (token) headers["authorization"] = `Bearer ${token}`;

  await fetch(`${AUTH_BASE}/auth/logout`, {
    method: "POST",
    credentials: "include",
    headers,
  }).catch(() => {});
  localStorage.removeItem("infra_token");
  window.location.reload();
}

/** Get service connection status (Google, etc.). */
export async function getConnections(): Promise<{ connections: Array<{ provider: string; provider_email: string; scopes: string; updated_at: string }> }> {
  const token = localStorage.getItem("infra_token");
  const headers: Record<string, string> = {};
  if (token) headers["authorization"] = `Bearer ${token}`;

  const res = await fetch(`${AUTH_BASE}/auth/connections`, {
    credentials: "include",
    headers,
  });
  if (!res.ok) return { connections: [] };
  return res.json();
}

/** Start Google service connection (Gmail, Calendar, Drive scopes). */
export function connectGoogle() {
  const token = localStorage.getItem("infra_token");
  const redirectUri = `${window.location.origin}/integrations`;
  window.location.href = `${AUTH_BASE}/auth/google/connect?token=${token}&redirect_uri=${encodeURIComponent(redirectUri)}`;
}

/** Extract token from URL hash after OAuth callback. */
export function extractTokenFromHash(): string | null {
  const hash = window.location.hash;
  const match = hash.match(/token=([^&]+)/);
  if (match) {
    const token = match[1];
    localStorage.setItem("infra_token", token);
    // Clean hash
    window.history.replaceState(null, "", window.location.pathname);
    return token;
  }
  return null;
}
